<script setup lang="ts">
import { activateRowPrimary, IconButton, KindIcon, MenuItem, MenuPopover, StatusTag } from '@taler/ui'
import { formatDate, formatRate } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import { signTone } from '@/app/accounting-view'
import { useDisplayCurrency } from '@/app/display-currency'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { entryTone, type TransactionEffect, type TransactionRow } from '../internal/transaction-row.ts'
import type { CrossLedgerLink } from '../composables/use-cross-ledger-links.ts'
import AccountSwatch from '@/features/transactions/components/AccountSwatch.vue'
import CrossLedgerLinkBadge from '@/features/transactions/components/CrossLedgerLinkBadge.vue'
import KindTile from '@/features/transactions/components/KindTile.vue'
import TransactionAmount from '@/features/transactions/components/TransactionAmount.vue'

const props = withDefaults(
  defineProps<{
    row: TransactionRow
    showDate: boolean
    canEdit: boolean
    canReverse: boolean
    crossLedgerLink?: CrossLedgerLink | undefined
    arrive?: boolean
  }>(),
  { arrive: false, crossLedgerLink: undefined },
)

const emit = defineEmits<{ edit: [string]; reverse: [string]; followCrossLedgerLink: [CrossLedgerLink] }>()

const { locale, t } = useI18n()
const route = useRoute()
const { displayCurrency } = useDisplayCurrency()

const entryLocation = computed(() => ({ query: { ...route.query, entry: props.row.id } }))

const dateLabel = computed(() => formatDate(props.row.date, locale.value as 'en' | 'hu'))
const isTransfer = computed(() => props.row.kind === 'TRANSFER')
const isMagnitude = computed(() => isTransfer.value && !props.row.isRevaluation)
// The icon already carries the kind, so the title is spent on the entry's own name and falls
// back to the kind only for an entry that has none.
const title = computed(() => {
  if (props.row.isRevaluation) return t('transactions.revaluation.title')
  return props.row.title || t(isTransfer.value ? 'transactions.transfer' : 'transactions.noDescription')
})
const tone = computed(() => {
  if (props.row.total === undefined) return undefined
  return isMagnitude.value ? 'transfer' : signTone(props.row.total)
})
const sourceLabel = computed(() =>
  props.row.source === undefined || props.row.source === 'MANUAL' || props.row.isRevaluation
    ? undefined
    : t(`transactions.source.${props.row.source}`),
)
const kindLabel = computed(() =>
  props.row.kind === undefined ? t('transactions.noKind') : t(`transactions.${props.row.kind.toLowerCase()}`),
)
const PILL_CLASS =
  'shrink-0 whitespace-nowrap rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-inline)] leading-[1.6]'
const fxRateLabel = computed(() => {
  const rateToBase = props.row.fx?.rateToBase
  return rateToBase === undefined
    ? undefined
    : formatRate(rateToBase, { locale: locale.value as 'en' | 'hu' })
})

const summaryTitle = computed(() => {
  const { from, to } = props.row.summary
  if (from === undefined) return undefined
  return to === undefined ? from.path : `${from.path} → ${to.path}`
})

// A stack shows each effect in the display currency only: other currencies have no converted figure here.
const stackedEffects = computed(() =>
  props.row.effects.filter((effect) => effect.amount.currency === displayCurrency.value),
)
const hiddenEffectCount = computed(() => props.row.effects.length - stackedEffects.value.length)
const isStacked = computed(() => props.row.effects.length > 1)

function effectKindLabel(effect: TransactionEffect): string {
  return t(`transactions.${effect.kind.toLowerCase()}`)
}
</script>

<template>
  <tr
    class="table-row cursor-pointer border-b border-[color:var(--color-border)]"
    :class="arrive ? 'row-arrive' : undefined"
    @click="activateRowPrimary"
  >
    <td
      class="h-[var(--density-row)] whitespace-nowrap px-[var(--space-cell)] align-middle text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
    >
      {{ showDate ? dateLabel : '' }}
    </td>
    <td class="h-[var(--density-row)] min-w-0 max-w-72 px-[var(--space-cell)] align-middle">
      <div class="flex min-w-0 items-center gap-[var(--space-cell)]">
        <KindTile :kind="row.kind" :label="kindLabel" />
        <div class="min-w-0">
          <p class="flex min-w-0 items-center gap-[var(--space-inline)]">
            <RouterLink
              :to="entryLocation"
              data-row-primary
              class="inline-flex min-w-0 items-center truncate py-[var(--space-tight)] text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)] underline decoration-transparent underline-offset-4 transition-colors duration-[var(--motion-fast)] hover:decoration-current"
            >
              {{ title }}
            </RouterLink>
          </p>
          <p
            v-if="
              sourceLabel !== undefined || row.isReversal || row.fx || row.tags.length > 0 || crossLedgerLink
            "
            class="flex min-w-0 flex-wrap items-center gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            <span v-if="sourceLabel !== undefined" :class="PILL_CLASS">{{ sourceLabel }}</span>
            <span v-if="row.fx">
              {{ t('transactions.crossLedgerTransfer') }}
              <template v-if="fxRateLabel"
                >· {{ t('transactions.rateToBase', { rate: fxRateLabel }) }}</template
              >
            </span>
            <StatusTag v-if="row.isReversal" tone="neutral">{{ t('transactions.reversed') }}</StatusTag>
            <CrossLedgerLinkBadge
              v-if="crossLedgerLink"
              :link="crossLedgerLink"
              @follow="emit('followCrossLedgerLink', $event)"
            />
            <span v-for="tag in row.tags" :key="tag" :class="PILL_CLASS">
              {{ tag }}
            </span>
          </p>
        </div>
      </div>
    </td>
    <td class="h-[var(--density-row)] min-w-0 max-w-64 px-[var(--space-cell)] align-middle">
      <div v-if="row.summary.from" class="flex min-w-0 flex-col gap-[var(--space-hair)]">
        <p
          class="flex min-w-0 items-center gap-[var(--space-tight)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          :title="summaryTitle"
        >
          <AccountSwatch :icon="row.summary.from.icon" :color="row.summary.from.color" />
          <span class="truncate">{{ row.summary.from.leaf }}</span>
          <template v-if="row.summary.to">
            <span aria-hidden="true" class="shrink-0 text-[color:var(--color-text-muted)]">→</span
            ><span class="sr-only">{{ t('transactions.transferTo') }}</span>
            <AccountSwatch :icon="row.summary.to.icon" :color="row.summary.to.color" />
            <span class="truncate">{{ row.summary.to.leaf }}</span>
          </template>
          <span
            v-if="row.summary.more > 0"
            class="shrink-0 whitespace-nowrap text-[color:var(--color-text-muted)]"
          >
            {{ t('transactions.summaryMore', { count: row.summary.more }) }}
          </span>
        </p>
      </div>
    </td>
    <td class="h-[var(--density-row)] px-[var(--space-cell)] text-right align-middle">
      <div v-if="isStacked" class="flex flex-col items-end">
        <p
          v-for="effect in stackedEffects"
          :key="`${effect.kind}:${effect.amount.currency}`"
          class="flex items-center gap-[var(--space-tight)]"
        >
          <KindIcon :kind="effect.kind" :label="effectKindLabel(effect)" />
          <MoneyAmount
            :value="effect.amount"
            :sign="effect.kind !== 'TRANSFER'"
            :tone="entryTone(effect.kind, effect.amount)"
          />
        </p>
        <p
          v-if="hiddenEffectCount > 0"
          class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
        >
          {{ t('transactions.effectsOtherCurrencies', { count: hiddenEffectCount }) }}
        </p>
      </div>
      <TransactionAmount
        v-else
        :total="row.total"
        :native-total="row.nativeTotal"
        :rate-missing="row.displayRateMissing"
        :tone="tone"
        :sign="!isMagnitude"
      />
    </td>
    <td class="h-[var(--density-row)] w-px px-[var(--space-cell)] align-middle">
      <MenuPopover side="bottom" align="end">
        <template #trigger>
          <IconButton :aria-label="t('transactions.rowActions', { entry: title })">
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
              <circle cx="3.5" cy="8" r="1.4" />
              <circle cx="8" cy="8" r="1.4" />
              <circle cx="12.5" cy="8" r="1.4" />
            </svg>
          </IconButton>
        </template>
        <MenuItem as-child>
          <RouterLink :to="entryLocation">{{ t('transactions.openAction') }}</RouterLink>
        </MenuItem>
        <MenuItem v-if="canEdit && !row.isRevaluation" @select="emit('edit', row.id)">
          {{ t('transactions.detail.editAction') }}
        </MenuItem>
        <MenuItem v-if="canReverse && !row.isRevaluation" @select="emit('reverse', row.id)">
          {{ t('transactions.detail.reverseAction') }}
        </MenuItem>
      </MenuPopover>
    </td>
  </tr>
</template>
